import { A11yModule } from '@angular/cdk/a11y';
import { ConnectedPosition, OverlayModule } from '@angular/cdk/overlay';
import { ChangeDetectionStrategy, Component, computed, input, signal } from '@angular/core';
import { LucideAngularModule } from 'lucide-angular';

/** Under the trigger, lined up on the side `align` names; above it when there is no room below. */
const POSITIONS: Record<'start' | 'end', ConnectedPosition[]> = {
  start: [
    { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 4 },
    { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -4 },
  ],
  end: [
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: 4 },
    { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -4 },
  ],
};

/**
 * An icon button that opens a panel under itself — the picker's filter and sort. What the panel
 * holds is projected in; this owns only opening it, and closing it again on Escape or a click
 * outside.
 *
 * The panel is a non-modal dialog: focus moves into it on open and goes back to whatever opened
 * it on close, which may be this trigger or something that called `open()` from elsewhere.
 */
@Component({
  selector: 'app-toolbar-popover',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [OverlayModule, A11yModule, LucideAngularModule],
  template: `
    <button
      type="button"
      class="dk-btn dk-btn--ghost dk-btn--secondary dk-btn--icon dk-btn--sm trigger"
      cdkOverlayOrigin
      #origin="cdkOverlayOrigin"
      aria-haspopup="dialog"
      [attr.aria-label]="label()"
      [attr.aria-expanded]="isOpen()"
      [attr.data-active]="active() || isOpen() ? '' : null"
      (click)="toggle()"
    >
      <lucide-icon [name]="icon()" [size]="16" aria-hidden="true" />
    </button>

    <ng-template
      cdkConnectedOverlay
      cdkConnectedOverlayHasBackdrop
      cdkConnectedOverlayBackdropClass="cdk-overlay-transparent-backdrop"
      cdkConnectedOverlayPanelClass="dk-overlay--popover"
      [cdkConnectedOverlayOrigin]="origin"
      [cdkConnectedOverlayOpen]="isOpen()"
      [cdkConnectedOverlayPositions]="positions()"
      (backdropClick)="close()"
      (overlayKeydown)="onOverlayKeydown($event)"
      (detach)="close()"
    >
      <div class="dk-popover panel" role="dialog" [attr.aria-label]="label()" cdkTrapFocus cdkTrapFocusAutoCapture>
        <ng-content />
      </div>
    </ng-template>
  `,
  styleUrl: './toolbar-popover.component.scss',
})
export class ToolbarPopoverComponent {
  /** The trigger's accessible name, and the panel's. */
  readonly label = input.required<string>();
  readonly icon = input.required<string>();
  readonly align = input<'start' | 'end'>('start');
  /** Whether what the panel controls is away from its default. */
  readonly active = input(false);

  readonly isOpen = signal(false);
  readonly positions = computed(() => POSITIONS[this.align()]);

  open(): void {
    this.isOpen.set(true);
  }

  close(): void {
    this.isOpen.set(false);
  }

  toggle(): void {
    this.isOpen.update((open) => !open);
  }

  onOverlayKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
    }
  }
}
